// src/gestion-logistics/types/costing.types.ts

export enum CostLineType {
  COST = "cost",
  REVENUE = "revenue",
  DISBURSEMENT = "disbursement",
}

export enum CostCategory {
  FREIGHT = "freight",
  HANDLING = "handling",
  CUSTOMS = "customs",
  INSURANCE = "insurance",
  STORAGE = "storage",
  DOCUMENTATION = "documentation",
  ADMINISTRATIVE = "administrative",
  OTHER = "other",
}

export enum AccountTypeCode {
  INCOME = "INCOME",
  EXPENSE = "EXPENSE",
  DISBURSEMENT = "DISBURSEMENT",
}

/**
 * Account Type (Type de compte)
 * 3 types fixes: INCOME, EXPENSE, DISBURSEMENT
 */
export interface AccountType {
  id: number;
  code: AccountTypeCode;
  name: string;
  description?: string;
  is_billable: boolean;
  affects_revenue: boolean;
  affects_cost: boolean;
  display_color?: string;
  display_icon?: string;
}

/**
 * Account Master (Rubrique comptable)
 * Remplace l'ancienne structure "Cost Labels"
 */
export interface AccountMaster {
  id: number;
  company_id: number;
  account_type_id: number;

  // Identification
  code: string;
  name: string;
  description?: string;

  // Configuration financière
  default_unit?: string;
  default_unit_price?: number;
  tax_applicable: boolean;
  default_tax_rate?: number;

  // Gestion débours
  markup_percentage?: number;
  markup_fixed_amount?: number;

  // État
  is_active: boolean;
  is_default: boolean;
  display_order: number;

  // Catégorisation
  category?: CostCategory;

  // Audit
  created_by: number;
  created_at: string;
  updated_by?: number;
  updated_at?: string;

  // Relations (chargées séparément)
  account_type?: AccountType;
  account_type_name?: string;
  account_type_code?: AccountTypeCode;
}

/**
 * Données de formulaire pour créer/modifier un Account Master
 */
export interface AccountMasterFormData {
  account_type_id: number;
  code: string;
  name: string;
  description?: string;
  default_unit?: string;
  default_unit_price?: number;
  tax_applicable?: boolean;
  is_active?: boolean;
}

/**
 * DEPRECATED: Ancienne structure Cost Label (kept for backward compatibility)
 * Utiliser AccountMaster à la place
 */
export interface CostLabel {
  id: number;
  company_id: number;
  code: string;
  name: string;
  description?: string;
  category: CostCategory;
  cost_type: "cost" | "revenue" | "both";
  default_unit?: string;
  is_active: boolean;
  display_order: number;
  created_by: number;
  created_at: string;
  updated_at?: string;
}

/**
 * Job Costing Line (Ligne de costing)
 */
export interface JobCostingLine {
  id: number;
  company_id: number;
  shipment_id: number;
  business_unit_id?: number;

  // Type et catégorie
  line_type: CostLineType;
  account_type_id?: number;
  account_master_id?: number;
  cost_label_id?: number; // DEPRECATED - kept for backward compatibility
  label_name: string;

  // Montants
  quantity?: number;
  unit?: string;
  unit_price: number;
  amount: number;
  currency_id?: number;

  // Fournisseur (si c'est un cost)
  supplier_id?: number;
  supplier_name?: string;
  supplier_reference?: string;

  // Client (si c'est un revenue)
  client_id?: number;
  client_name?: string;
  client_reference?: string;

  // Facturation client (si c'est un revenue)
  is_invoiced: boolean;
  invoice_id?: number;
  invoice_number?: string;
  proforma_invoice_id?: number;
  proforma_invoice_number?: string;

  // Facturation fournisseur (si c'est un cost) - PHASE 2
  is_supplier_billed?: boolean;
  supplier_bill_id?: number;
  supplier_bill_number?: string;
  supplier_billed_at?: string;
  supplier_billed_by_name?: string;

  // Détails
  description?: string;

  // Audit
  created_by: number;
  created_at: string;
  updated_by?: number;
  updated_at?: string;

  // Relations (chargées séparément)
  account_master?: AccountMaster;
  account_type_code?: AccountTypeCode;
  account_type_name?: string;
  currency_symbol?: string;
}

/**
 * Form data for creating/editing job costing line
 */
export interface JobCostingFormData {
  shipment_id: number;
  business_unit_id?: number;
  line_type: CostLineType;
  account_master_id?: number;
  cost_label_id?: number; // DEPRECATED
  label_name: string;
  quantity?: number;
  unit?: string;
  unit_price: number;
  currency_id?: number;
  supplier_id?: number;
  supplier_name?: string;
  supplier_reference?: string;
  client_id?: number;
  client_name?: string;
  client_reference?: string;
  description?: string;
}

/**
 * Job Costing Summary (Récapitulatif financier)
 */
export interface JobCostingSummary {
  shipment_id: number;
  shipment_number: string;
  total_cost: number;
  total_revenue: number;
  profit_margin: number;
  profit_margin_percentage: number;

  // Détails par type de ligne
  lines_by_type?: {
    line_type: CostLineType;
    count: number;
    total_amount: number;
  }[];

  // Détails par type de compte
  lines_by_account_type?: {
    account_type_code: AccountTypeCode;
    account_type_name: string;
    count: number;
    total_amount: number;
  }[];

  // Statut facturation
  invoiced_lines_count?: number;
  uninvoiced_lines_count?: number;
  total_invoiced_amount?: number;
}
